import { onScopeDispose, shallowRef, triggerRef } from "vue";

/** Bar-owned connections, separate from the DOM-derived slotted-filter registry. */
export function useFinderContributions() {
  const contributions = shallowRef(new Map());
  const connections = new Map();
  let disposed = false;

  /**
   * @param {import('#ce/components/SpFinder/engine/finder-contribution-protocol').FinderContribution} descriptor
   * @param {{signal: AbortSignal}} options
   * @returns {import('#ce/components/SpFinder/engine/finder-contribution-protocol').FinderContributionConnection}
   */
  function connectContribution(descriptor, { signal } = {}) {
    validateConnection(descriptor, signal);
    const entry = { ...descriptor, actions: { ...descriptor.actions }, state: { ...descriptor.state } };
    const connection = createConnection(entry, {
      signal,
      closed: disposed || signal.aborted,
      onStateChange: notifyContributions,
      onDisconnect: removeConnection,
    });
    if (!connection.closed) {
      registerConnection(entry, connection, signal);
    }
    return connection;
  }

  function registerConnection(entry, connection, signal) {
    const previous = connections.get(entry.id);
    // Replacing in place preserves menu order when metadata changes.
    connections.set(entry.id, connection);
    disconnectConnection(previous);
    contributions.value.set(entry.id, entry);
    signal.addEventListener("abort", connection.disconnect, { once: true });
    triggerRef(contributions);
  }

  function removeConnection(id, connection) {
    if (connections.get(id) !== connection) {
      return;
    }
    connections.delete(id);
    contributions.value.delete(id);
    triggerRef(contributions);
  }

  function dispose() {
    disposed = true;
    [...connections.values()].forEach(disconnectConnection);
  }

  function notifyContributions() {
    triggerRef(contributions);
  }

  onScopeDispose(dispose);
  return { contributions, connectContribution };
}

function validateConnection(descriptor, signal) {
  if (!signal || typeof signal.addEventListener !== "function") {
    throw new TypeError("connectContribution requires a lifetime AbortSignal");
  }
  if (typeof descriptor?.id !== "string" || !descriptor.id.trim()) {
    throw new TypeError("A finder contribution requires a nonempty string id");
  }
  validateActions(descriptor.actions);
  validateState(descriptor.state);
}

function validateState(state) {
  if (
    typeof state?.active !== "boolean" ||
    typeof state.withheld !== "boolean" ||
    typeof state.clearable !== "boolean"
  ) {
    throw new TypeError("A finder contribution state requires active, withheld and clearable booleans");
  }
}

function validateActions(actions) {
  if (typeof actions?.clear !== "function") {
    throw new TypeError("A finder contribution requires an actions.clear function");
  }
  if (actions.reveal !== undefined && typeof actions.reveal !== "function") {
    throw new TypeError("A finder contribution actions.reveal must be a function when provided");
  }
}

function sameState(current, next) {
  return current.active === next.active && current.withheld === next.withheld && current.clearable === next.clearable;
}

function createConnection(entry, { signal, closed, onStateChange, onDisconnect }) {
  const connection = {
    get closed() {
      return closed;
    },
    updateState,
    disconnect,
  };

  function updateState(state) {
    if (closed) {
      return;
    }
    validateState(state);
    if (sameState(entry.state, state)) {
      return;
    }
    entry.state = { ...state };
    onStateChange();
  }

  function disconnect() {
    if (closed) {
      return;
    }
    closed = true;
    signal.removeEventListener("abort", disconnect);
    onDisconnect(entry.id, connection);
  }

  return connection;
}

function disconnectConnection(connection) {
  connection?.disconnect();
}
